import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { duracion } from '@/modules/tablero/duracion'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { fecha } from '@/shared/utilidades/formato'

import { DesgloseFases } from './DesgloseFases'
import { useServiciosFinalizados } from './useServiciosFinalizados'
import { useActaFirmada } from './useActaFirmada'
import estilos from './PaginaServiciosFinalizados.module.css'

/**
 * El archivo de servicios.
 *
 * Sección 6. Lo que ya recorrió las nueve estaciones, con las horas que pasó
 * en cada una. El desglose no se guarda en ninguna columna: se reconstruye de
 * la bitácora cada vez, así que no puede quedar desactualizado ni contradecir
 * la historia del documento.
 */
export function PaginaServiciosFinalizados() {
  const [termino, setTermino] = useState('')
  const [abierto, setAbierto] = useState<string | null>(null)
  const { servicios, cargando, error } = useServiciosFinalizados(termino)

  return (
    <Tarjeta
      subtitulo="Buscable por cliente, por orden de servicio o por orden de trabajo"
      acciones={
        <input
          className={estilos.buscador}
          type="search"
          placeholder="Cliente, OS-… u OT-…"
          value={termino}
          onChange={(e) => setTermino(e.target.value)}
          aria-label="Buscar en el archivo"
        />
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      {cargando ? (
        <Cargando mensaje="CONSULTANDO SERVICIOS FINALIZADOS..." />
      ) : servicios.length === 0 ? (
        <p className={estilos.vacio}>
          {termino.trim()
            ? 'Ningún servicio finalizado con ese dato.'
            : 'Todavía no hay servicios finalizados. Aparecen aquí cuando el acta queda firmada.'}
        </p>
      ) : (
        <>
        <div className={estilos.encabezados} aria-hidden="true">
          <span>Cliente y documentos</span>
          <span>Servicio</span>
          <span className={estilos.derecha}>De punta a punta</span>
          <span />
        </div>

        <ul className={estilos.lista}>
          {servicios.map((servicio) => (
            <Ficha
              key={servicio.orden_servicio_id}
              servicio={servicio}
              abierto={abierto === servicio.orden_servicio_id}
              onAbrir={() =>
                setAbierto(
                  abierto === servicio.orden_servicio_id ? null : servicio.orden_servicio_id,
                )
              }
            />
          ))}
        </ul>
        </>
      )}
    </Tarjeta>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

type PropsFicha = {
  servicio: ReturnType<typeof useServiciosFinalizados>['servicios'][number]
  abierto: boolean
  onAbrir: () => void
}

function Ficha({ servicio, abierto, onAbrir }: PropsFicha) {
  const navegar = useNavigate()
  // El acta solo se busca cuando la tarjeta esta abierta.
  const { acta, cargando: cargandoActa } = useActaFirmada(servicio.orden_trabajo_numero ?? null, abierto)

  return (
    <li className={`${estilos.ficha} ${abierto ? estilos.fichaAbierta : ''}`}>
      <div
        className={estilos.encabezado}
        role="button"
        tabIndex={0}
        onClick={onAbrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onAbrir()
          }
        }}
      >
        <div className={estilos.identidad}>
          <p className={estilos.cliente}>{servicio.cliente_nombre ?? 'Sin cliente'}</p>
          <p className={estilos.numeros}>
            <span>{servicio.numero}</span>
            {/* Pueden ser varias —la principal y sus adicionales— y vienen
                juntas en un solo texto: cada una es su propia etiqueta. */}
            {servicio.numero_cotizacion
              ?.split(' · ')
              .map((cot) => <span key={cot}>{cot}</span>)}
            {servicio.orden_trabajo_numero && <span>{servicio.orden_trabajo_numero}</span>}
          </p>
        </div>

        <div className={estilos.datos}>
          <span className={estilos.servicio}>
            {servicio.servicio_codigo && (
              <span className={estilos.codigo}>{servicio.servicio_codigo}</span>
            )}
            {servicio.servicio_nombre ?? '—'}
          </span>

          <span className={estilos.cerrado}>Finalizado el {fecha(servicio.finalizado_en)}</span>
        </div>

        <div className={estilos.total}>
          <span className={estilos.totalCifra}>{duracion(servicio.horas_totales)}</span>
          <span className={estilos.totalRotulo}>de punta a punta</span>
        </div>

        <span className={estilos.ver}>{abierto ? 'Cerrar' : 'Ver las fases'}</span>
      </div>

      {abierto && (
        <div className={estilos.detalle}>
          <DesgloseFases
            estaciones={servicio.estaciones ?? []}
            numeros={[servicio.numero, servicio.numero_cotizacion, servicio.orden_trabajo_numero]}
          />

          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.enlace}
              onClick={() => navegar(`/ordenes/${servicio.orden_servicio_id}`)}
            >
              Abrir el documento
            </button>

            {acta?.url && (
              <a className={estilos.enlace} href={acta.url} target="_blank" rel="noopener noreferrer">
                Ver el acta firmada
              </a>
            )}

            {/* EN EL MISMO RENGLÓN QUE LOS BOTONES. Johan: «¿por qué está en
                2 filas?». El estado del acta iba en una caja aparte, debajo,
                ocupando todo el ancho para una sola línea de texto. */}
            {/* El acta firmada sí se archiva: el PDF vive en el bucket privado
                'actas' y se abre con un enlace temporal. */}
            {cargandoActa ? (
              <p className={estilos.nota}>Buscando el acta firmada…</p>
            ) : acta ? (
              <p className={estilos.nota}>
                Firmada por el cliente{acta.firmadoEn ? ` el ${fecha(acta.firmadoEn)}` : ''}
                {acta.calificacion != null ? ` · calificó ${acta.calificacion} de 5` : ''}
                {acta.observaciones ? ` · «${acta.observaciones}»` : ''}
              </p>
            ) : (
              <p className={estilos.nota}>
                Este servicio no tiene acta firmada archivada.
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  )
}
